'use client'
/***
 *
 * @file components/IframeWrapper/index.tsx
 * @description IframeWrapper component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Truminds Software System
 * @since 13 Feb 2024
 *
 */

import React, { useEffect, useState } from "react";
// import "./IframeWrapper.scss";
import Iframe from "../Iframe";
import { LandingSkeleton } from "@/blocks/Landing/LandingSkeleton";
import { IFRAME_CATEGORY_ID } from "@/config/app.config";
import { Box } from "@/layouts/LayoutComponents";
import classNames from "classnames";
import { Body } from "@/client/foundations/Typography";
import Link from "next/link";
import Spinner from "../Spinner";

type Props = {
  title?: string,
  categoryId?: string,
  acceptCookieMsg?: string,
  src: string | undefined,
  allowFullScreen?: boolean,
  loading?: "lazy" | "eager",
  height?: string | undefined,
  width?: string | undefined
  //height and width used in html renderer
}

const IframeWrapper = ({ title, height, width, src, acceptCookieMsg = "Please accept cookies to view. If you do not want to accept cookies try", categoryId = IFRAME_CATEGORY_ID, allowFullScreen = true, loading = "lazy", ...props }: Props) => {

  const isInContentfulStudio = (typeof window !== "undefined") && window.self !== window.top;

  const host = window?.location?.hostname;

  const [buffering, setBuffering] = useState(true);
  const [source, setSource] = useState<string | null>(null);
  const [cookieAccepted, setCookieAccepted] = useState(false);

  useEffect(() => {
    const checkCookie = () => {
      const activeGroups = (window as any).OptanonActiveGroups || "";
      if (activeGroups?.includes(categoryId)) {
        setCookieAccepted(true);
      }
    }

    if (typeof window !== 'undefined' && typeof (window as any).OptanonActiveGroups === 'string') {
      checkCookie();
    }

    // Listen for consent changes dynamically
    const onConsentChanged = () => {
      checkCookie();
    };

    document.addEventListener('consent.onetrust', onConsentChanged);
    return () => {
      document.removeEventListener('consent.onetrust', onConsentChanged);
    };
  }, [categoryId]);

  useEffect(() => {
    let newSrc: string | null = src || null;

    if (src?.includes("parent=www.example.com")) {
      newSrc = src?.replace("parent=www.example.com", `parent=${host}`);
      if (isInContentfulStudio) {
        newSrc = newSrc + "&parent=app.contentful.com"
      }
    }
    setSource(newSrc);

  }, [src])


  const IframePlaceholder = ({ width = 10, height = 0, state = "loading" }: { width?: number, height?: number, state?: string }) => {
    return <Box
      component="div"
      hAlignContent="center"
      vAlignContent="center"
      column
      className={classNames(
        // "relative",
        // "w-[100vw]",
        "w-full h-full",
        // "!aspect-16/9",
        "bg-[#D3D3D3]",
        "absolute",
        "z-[1]"
      )}
    >
      {
        state === "loading" ?
          <Spinner loading={state === "loading"} skipTimer={true} fullPage={false}><></></Spinner>
          :
          <>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="48"
              height="48"
              viewBox="0 0 2048 2048"
              style={{
                shapeRendering: 'geometricPrecision',
                textRendering: 'geometricPrecision',
                imageRendering: 'auto',
                fillRule: 'evenodd',
                clipRule: 'evenodd',
              }}
            >
              <g id="Layer_x0020_1">
                <g id="_461238856">
                  <path
                    d="M1186.02 320.696 1526.412 915.162 1865.656 1507.626l1 2c16.646 34.451 23.12 68.792 21.591 101.084-1.724 36.423-13.58 69.932-32.511 97.921-18.941 28.007-45.044 50.614-75.239 65.204-26.27 12.692-55.652 19.296-86.152 18.105v.062H350.283l-1.125-.125c-30.57-1.09-59.03-8.677-84.071-21.365-32.373-16.404-59.113-41.322-77.45-71.8-18.294-30.401-28.137-66.282-26.767-104.689 1.055-29.56 8.732-60.424 24.301-91.152l.75-1.5L521.682 910.482l342.472-598.1 3.5-4.874c14.66-17.115 30.566-31.35 47.244-42.792 38.051-26.106 80.12-37.695 120.9-35.901 41.145 1.812 80.65 17.23 113.185 45.101 13.23 11.333 25.277 24.688 35.764 39.975l1.374 2.125z"
                    fill="#959595"
                  />
                  <path
                    d="M1104.01 370.659c-4.678-7.21-11.079-13.587-17.577-19.155-15.59-13.355-34.434-21.2-54.955-22.103-22.483-.989-43.958 6.523-62.369 19.155-7.017 4.814-18.544 12.617-23.571 19.62l-.624.868-674.312 1177.63c-7.788 15.806-13.16 33.073-13.793 50.767-.65 18.211 3.672 36.128 13.086 51.772 9.218 15.321 22.64 27.581 38.585 35.66 12.887 6.53 32.158 9.369 47.216 11.125l1342.4.012c14.096.55 27.957-2.488 40.644-8.618 15.078-7.285 28.108-18.685 37.484-32.547 9.806-14.5 15.31-31.2 16.138-48.682.885-18.681-3.062-36.463-11.355-53.19l-676.993-1182.32z"
                    fill="#fbfbfc"
                  />
                </g>
                <path
                  d="M1024.6 1303.87c15.718 0 30.53 2.857 44.401 8.6 13.812 5.717 26.293 14.168 37.405 25.377 11.25 11.25 19.745 23.77 25.454 37.522 5.768 13.898 8.669 28.781 8.669 44.615 0 15.755-2.899 30.535-8.66 44.335-5.722 13.708-14.222 26.186-25.463 37.427-11.112 11.209-23.592 19.66-37.404 25.377-13.872 5.743-28.683 8.6-44.402 8.6-15.678 0-30.464-2.846-44.355-8.571-13.812-5.69-26.327-14.116-37.538-25.304l-.1-.102c-11.174-11.272-19.635-23.767-25.317-37.476-5.724-13.804-8.616-28.57-8.616-44.286 0-15.575 2.863-30.365 8.586-44.272 5.644-13.71 13.998-26.204 25.057-37.385 11.171-11.391 23.668-19.981 37.518-25.769 13.865-5.792 28.779-8.688 44.765-8.688zM1036.71 1271.63h-47.89l-1.444-24.252c-1.26-21.202-3.884-44.38-7.866-69.577l-.015.002c-4.063-25.683-9.559-53.75-16.485-84.242l-28.297-124.56c-8.485-37.695-14.92-70.33-19.282-97.96-4.5-28.494-6.761-51.328-6.761-68.547 0-17.381 2.58-33.411 7.71-48.082 5.285-15.108 13.269-28.476 23.918-40.096l.101-.1c10.91-11.776 23.509-20.554 37.728-26.394 14.178-5.826 29.594-8.706 46.192-8.706 16.41 0 31.578 2.914 45.559 8.772 14.13 5.92 26.71 14.716 37.793 26.414l-.003.004c10.975 11.488 19.205 24.634 24.715 39.407 5.438 14.575 8.142 30.325 8.142 47.218 0 15.656-2.4 37.808-7.176 66.482-4.541 27.254-11.394 61.112-20.532 101.588v.102l-28.668 124.559c-4.93 21.482-9.191 44.956-12.8 70.46-3.595 25.424-6.582 53.333-8.977 83.76l-1.87 23.748h-23.792z"
                  fill="#959595"
                />
                <path fill="none" d="M0 0h2048v2048H0z" />
              </g>
            </svg>
            <div className="mb-[32px] flex flex-col align-middle items-center text-center p-3 gap-2" style={{
              wordBreak: "break-all"
            }}>

              <Body size="m" className="mr-[4px]">
                {acceptCookieMsg}
              </Body>
              <Link aria-label={source as string} className="hover:!bg-gray-300 btn-link" href={source === null ? "" : source} target="_blank">
                <Body size="m">
                  {source}
                </Body>
              </Link>
            </div>
          </>
      }
    </Box>
  }

  return (
    <div className="IframeWrapper-container" data-testid="IframeWrapper">
      {(!cookieAccepted || buffering) && <IframePlaceholder state={!cookieAccepted ? "cookieConsent" : "loading"} />}
      {source !== null &&
        <Iframe height={height} width={width} categoryId={categoryId} onLoad={() => { setBuffering(false) }} src={source} allowFullScreen={allowFullScreen} loading={loading} title={title || "iframe"} {...props}></Iframe>
      }
      {/* <pre>kkkk -- {JSON.stringify(iFrameHeight, null, 2)}</pre> */}
    </div>
  );
};

export default IframeWrapper;
